import { SxProp, View } from 'dripsy';
import React, { useContext } from 'react';

import { UniButton } from '@/design-system/components/buttons';
import { InstructionButton } from '@/design-system/components/buttons/instructionButton/InstructionButton';
import { UniNotes } from '@/design-system/components/notes/UniNote';
import { ProgressStatus, UniProgressCard } from '@/design-system/components/progress';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { CurrenciesIcon } from '../../components';
import { useTranslation } from 'react-i18next';
import { CurrenciesContext } from '@/currencies/currency.context';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { useNavigation } from '@react-navigation/native';
import { EarnStackParamList, EarnStackRoutes } from '../../earn.navigator';
import { UserEarnActionListResponseDataItem } from '@utx/utx-client';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { SubscriptionDetailsStackParamList, SubscriptionDetailsStackRoutes } from './type';
import { LayoutChangeEvent } from 'react-native';
import { LayoutContext } from 'contexts/LayoutContext';

export interface SubscriptionBodyProps {
  statusData: Array<ProgressStatus>;
  subscription: UserEarnActionListResponseDataItem;
  onLayout: (event: LayoutChangeEvent) => void;
  onUpdate: () => void;
  sx?: SxProp;
}

export const SubscriptionBody: React.FC<SubscriptionBodyProps> = ({ statusData, subscription, onLayout, onUpdate, sx }) => {
  const { t } = useTranslation('earn');
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const navigation = useNavigation<NativeStackNavigationProp<SubscriptionDetailsStackParamList>>();
  const earnNavigation = useNavigation<NativeStackNavigationProp<EarnStackParamList>>();
  const layout = useContext(LayoutContext);
  const tenor = getEarnTenorTranslationKey(subscription.tenor);

  const handleUpdateAutoRenewal = () => {
    navigation.navigate(SubscriptionDetailsStackRoutes.AutoRenew, {
      subscription,
      onLayout,
      onRefresh: onUpdate
    });
  };

  const handleRedeemAsset = () => {
    navigation.navigate(SubscriptionDetailsStackRoutes.Redeem, {
      subscription,
      onLayout,
      onUpdate
    });
  };

  const Notes = {
    LOCKED: [t('earn-invest::glossary.subscription-note'), t('earn-invest::glossary.subscription-note-early-redemption')],
    FLEXIBLE: [t('earn-invest::glossary.subscription-note')]
  };

  const formattedData = {
    subscriptionAmount: formatAmountByDecimals({
      value: subscription.amount,
      decimals: getDecimalByAsset(subscription.asset),
      suffix: subscription.asset
    }),
    subscriptionAmountUsd: formatAmountByDecimals({
      value: subscription.amountUsd,
      decimals: 2,
      prefix: '≈',
      suffix: 'USD'
    }),
    estimatedApyPercent: formatAmountByDecimals({ value: subscription.estimatedApyPercent, decimals: 2, suffix: '%' }),
    rewardsAccrued: formatAmountByDecimals({
      value: subscription.rewardsAccrued,
      decimals: getDecimalByAsset(subscription.asset),
      suffix: subscription.asset
    }),
    rewardsAccruedUsd: formatAmountByDecimals({
      value: subscription.rewardsAccruedUsd,
      decimals: 2,
      prefix: '≈',
      suffix: 'USD'
    }),
    tenor: t(tenor.key, tenor.options)
  };

  return (
    <View sx={{ ...styles.modalBody, padding: layout.isMobile ? '$4' : '$7', ...sx }}>
      <View>
        <CurrenciesIcon asset={subscription.asset} title={subscription.offerName} subText={subscription.provider} sx={{ mb: '$7' }} />
        <TextBody sx={{ mb: '$7', textAlign: 'right' }}>{t('earn-invest::glossary.ref-id-number', { refId: subscription.refId })}</TextBody>
        <ModalBodyRow sx={{ mb: '$7' }}>
          <View sx={styles.amountBox}>
            <TextCaption bold secondary>
              {t('earn-invest::glossary.principle')}
            </TextCaption>
            <H6 bold sx={{ color: '$secondary' }}>
              {formattedData.subscriptionAmount}
            </H6>
            <TextCaption secondary>{formattedData.subscriptionAmountUsd}</TextCaption>
          </View>
          <View sx={{ ...styles.amountBox, alignItems: 'flex-end' }}>
            <TextCaption bold secondary>
              {t('earn-invest::glossary.est-apy')}
            </TextCaption>
            <H6 bold>{formattedData.estimatedApyPercent}</H6>
          </View>
        </ModalBodyRow>
        <ModalBodyRow sx={{ mb: '$7' }}>
          <View sx={styles.amountBox}>
            <TextCaption bold secondary>
              {t('earn-invest::glossary.interest')}
            </TextCaption>
            <H6 bold sx={{ color: '$success' }}>
              {formattedData.rewardsAccrued}
            </H6>
            <TextCaption secondary>{formattedData.rewardsAccruedUsd}</TextCaption>
          </View>
          <View sx={{ ...styles.amountBox, alignItems: 'flex-end' }}>
            <TextCaption bold secondary>
              {t('earn-invest::glossary.tenor')}
            </TextCaption>
            <H6 bold>{formattedData.tenor}</H6>
          </View>
        </ModalBodyRow>
        <View sx={styles.progressBox}>
          <UniProgressCard
            sx={{ paddingX: '$4', paddingY: '$3', backgroundColor: '$backgroundSecondary', borderRadius: 10 }}
            statusData={statusData}
          />
        </View>
        <View sx={styles.instructionButtonGroup}>
          {/* If Locked, allow auto renew */}
          {subscription.maturityDate && subscription.autoRenewable ? (
            <InstructionButton
              title={t('earn-invest::glossary.auto-renewal')}
              text={
                subscription.autoRenew ? t('earn-invest::glossary.disable-auto-description') : t('earn-invest::glossary.auto-description')
              }
              label={subscription.autoRenew ? t('earn-invest::glossary.disable') : t('earn-invest::glossary.enable')}
              onPress={handleUpdateAutoRenewal}
            />
          ) : null}
          {subscription.callable ? (
            <InstructionButton
              title={t('earn-invest::glossary.redeem')}
              text={t('earn-invest::glossary.redeem-description')}
              label={t('earn-invest::glossary.redeem')}
              onPress={handleRedeemAsset}
            />
          ) : null}
        </View>
        <View sx={{ height: '$5' }} />
        <UniNotes
          title={t('earn-invest::glossary.note')}
          paragraphs={Notes[subscription.maturityDate ? 'LOCKED' : 'FLEXIBLE']}
          isList={!!subscription.maturityDate}
        />
      </View>
      {!subscription.maturityDate && (
        <UniButton
          size="default"
          sx={{ mt: '$5' }}
          label={t('earn-invest::glossary.subscription-more')}
          onPress={() => {
            navigation.goBack();
            earnNavigation.navigate(EarnStackRoutes.Subscribe, {
              offerId: subscription.offerId
            });
          }}
        />
      )}
    </View>
  );
};

const ModalBodyRow: React.FC<{ sx?: SxProp }> = ({ sx, children }) => {
  return <View sx={{ ...styles.modalBodyRow, ...sx }}>{children}</View>;
};

enum StyleName {
  container = 'container',
  modalBody = 'modalBody',
  modalBodyRow = 'modalBodyRow',
  amountBox = 'amountBox',
  progressBox = 'progressBox',
  instructionButtonGroup = 'instructionButtonGroup'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    width: 500
  },
  modalBody: {
    flexGrow: 1
  },
  modalBodyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  amountBox: {
    gap: '$1'
  },
  progressBox: {},
  instructionButtonGroup: {
    flex: 1,
    paddingY: '$5',
    gap: '$5'
  }
};
